import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import api from '../utils/api';
import { toast } from 'sonner';
import { ClipboardCheck, Plus, Pencil, Trash2, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { ReportTasksView } from './ReportTasksView';

const emptyForm = () => ({ title: '', date: new Date().toISOString().slice(0, 10), check: '', how_to: '' });

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('it-IT'); } catch { return iso; }
};

const ItemForm = ({ form, setForm, onSave, onCancel, saving }) => (
  <div className="space-y-2 rounded-lg border border-teal-200 bg-teal-50/60 p-3" data-testid="checklist-item-form">
    <div className="flex flex-col sm:flex-row gap-2">
      <Input
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        placeholder="Titolo (es. Nuova funzione X)"
        className="bg-white"
        data-testid="checklist-form-title"
      />
      <Input
        type="date"
        value={form.date}
        onChange={(e) => setForm({ ...form, date: e.target.value })}
        className="bg-white sm:w-44"
        data-testid="checklist-form-date"
      />
    </div>
    <Textarea
      value={form.check}
      onChange={(e) => setForm({ ...form, check: e.target.value })}
      placeholder="Cosa controllare..."
      rows={2}
      className="bg-white"
      data-testid="checklist-form-check"
    />
    <Textarea
      value={form.how_to}
      onChange={(e) => setForm({ ...form, how_to: e.target.value })}
      placeholder="Come svolgere il controllo, passo per passo..."
      rows={3}
      className="bg-white"
      data-testid="checklist-form-how-to"
    />
    <div className="flex gap-2 justify-end">
      <Button size="sm" variant="outline" onClick={onCancel} data-testid="checklist-form-cancel">Annulla</Button>
      <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={onSave} disabled={saving} data-testid="checklist-form-save">
        {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}Salva
      </Button>
    </div>
  </div>
);

const VerificationChecklist = () => {
  const [data, setData] = useState(null);
  const [showDone, setShowDone] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [tasks, setTasks] = useState({ open: [], todo: [] });
  const [activeView, setActiveView] = useState('checks');
  const [expandedHowTo, setExpandedHowTo] = useState(null);

  const load = useCallback(async () => {
    try {
      const [res, taskRes] = await Promise.all([
        api.get('/admin/verification-checklist', { timeout: 20000 }),
        api.get('/admin/verification-checklist/tasks', { timeout: 20000 }),
      ]);
      setData(res.data); setTasks(taskRes.data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleDone = async (item, done) => {
    try {
      await api.put(`/admin/verification-checklist/${item.id}`, { done });
      toast.success(done ? 'Controllata ✓ — rimossa anche dal report PDF' : 'Riportata tra le cose da controllare');
      load();
    } catch { toast.error('Errore nel salvataggio'); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Eliminare "${item.title}"?`)) return;
    try {
      await api.delete(`/admin/verification-checklist/${item.id}`);
      toast.success('Voce eliminata');
      load();
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Il titolo è obbligatorio'); return; }
    setSaving(true);
    try {
      if (editingId) await api.put(`/admin/verification-checklist/${editingId}`, form);
      else await api.post('/admin/verification-checklist', form);
      toast.success(editingId ? 'Voce aggiornata' : 'Voce aggiunta');
      setAdding(false); setEditingId(null); setForm(emptyForm());
      load();
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  const startEdit = (item) => {
    setAdding(false);
    setEditingId(item.id);
    setForm({ title: item.title, date: item.date || '', check: item.check || '', how_to: item.how_to || '' });
  };

  if (!data) return null;
  const { pending = [], done = [] } = data;

  const renderRow = (item, isDone) => (
    <div key={item.id} className="group flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 hover:border-teal-300 transition-colors" data-testid={`checklist-item-${item.id}`}>
      <Checkbox
        checked={isDone}
        onCheckedChange={(v) => toggleDone(item, Boolean(v))}
        className="mt-0.5 data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-600"
        data-testid={`checklist-checkbox-${item.id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-sm font-semibold ${isDone ? 'line-through text-slate-400' : 'text-slate-800'}`}>{item.title}</span>
          <span className="text-[11px] font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5">{fmtDate(item.date)}</span>
        </div>
        {item.check && <p className={`text-xs mt-1 ${isDone ? 'text-slate-400' : 'text-slate-600'}`} data-testid={`checklist-objective-${item.id}`}>{item.check}</p>}
        {item.how_to && <div className="mt-2">
          <button onClick={() => setExpandedHowTo(expandedHowTo === item.id ? null : item.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900" data-testid={`checklist-how-to-toggle-${item.id}`}>
            {expandedHowTo === item.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} Come svolgere il controllo
          </button>
          {expandedHowTo === item.id && <div className="mt-2 whitespace-pre-line border-l-2 border-blue-300 bg-blue-50 px-3 py-2 text-xs leading-5 text-slate-700" data-testid={`checklist-how-to-${item.id}`}>{item.how_to}</div>}
        </div>}
      </div>
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => startEdit(item)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`checklist-edit-${item.id}`}>
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => remove(item)} className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600" data-testid={`checklist-delete-${item.id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );

  return (
    <Card className="p-6 border-0 shadow-lg bg-white/90 backdrop-blur-sm border-l-4 !border-l-teal-500" data-testid="verification-checklist-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-teal-600 to-teal-500">
            <ClipboardCheck className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              Controlli e attività dal report
              {pending.length > 0 && (
                <span className="text-xs font-bold text-white bg-teal-600 rounded-full px-2 py-0.5" data-testid="checklist-pending-count">{pending.length}</span>
              )}
            </h3>
            <p className="text-xs text-slate-500">Un’unica fonte per verifiche operative e task ancora aperti</p>
          </div>
        </div>
        {activeView === 'checks' && <Button
          size="sm"
          variant="outline"
          className="border-teal-300 text-teal-700 hover:bg-teal-50"
          onClick={() => { setEditingId(null); setForm(emptyForm()); setAdding(!adding); }}
          data-testid="checklist-add-btn"
        >
          <Plus className="h-4 w-4 mr-1" />Aggiungi voce
        </Button>}
      </div>

      <div className="mb-4 flex gap-2 border-b border-slate-200" data-testid="report-dashboard-tabs">
        <button onClick={() => setActiveView('checks')} className={`border-b-2 px-3 py-2 text-sm font-semibold ${activeView === 'checks' ? 'border-teal-600 text-teal-800' : 'border-transparent text-slate-500'}`} data-testid="report-dashboard-checks-tab">Cose da controllare ({pending.length})</button>
        <button onClick={() => setActiveView('tasks')} className={`border-b-2 px-3 py-2 text-sm font-semibold ${activeView === 'tasks' ? 'border-teal-600 text-teal-800' : 'border-transparent text-slate-500'}`} data-testid="report-dashboard-tasks-tab">Task da fare ({(tasks.open?.length || 0) + (tasks.todo?.length || 0)})</button>
      </div>

      {activeView === 'tasks' ? <ReportTasksView tasks={tasks} /> : <>
      {adding && <div className="mb-3"><ItemForm form={form} setForm={setForm} onSave={save} onCancel={() => setAdding(false)} saving={saving} /></div>}

      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="checklist-pending-list">
        {pending.length === 0 && !adding && (
          <p className="text-sm text-slate-500 py-2">Tutto controllato 🎉 — nessuna voce in sospeso.</p>
        )}
        {pending.map((item) => (
          editingId === item.id
            ? <ItemForm key={item.id} form={form} setForm={setForm} onSave={save} onCancel={() => setEditingId(null)} saving={saving} />
            : renderRow(item, false)
        ))}
      </div>

      {done.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <button
            onClick={() => setShowDone(!showDone)}
            className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-teal-700"
            data-testid="checklist-toggle-done"
          >
            {showDone ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            Controllate ({done.length})
          </button>
          {showDone && (
            <div className="space-y-2 mt-2 max-h-[260px] overflow-y-auto pr-1" data-testid="checklist-done-list">
              {done.map((item) => (
                editingId === item.id
                  ? <ItemForm key={item.id} form={form} setForm={setForm} onSave={save} onCancel={() => setEditingId(null)} saving={saving} />
                  : renderRow(item, true)
              ))}
            </div>
          )}
        </div>
      )}
      </>}
    </Card>
  );
};

export default VerificationChecklist;
